
/**
 * Algolia Service Class. 
 * POC: Anirudh Rath & Vikram Chand
 * Initial Author: Anirudh Rath
 */
import { acceptHMRUpdate, defineStore } from 'pinia';
import { AlgoliaService } from '~/services/algolia.service';
import {
    type options, type Filters,
    type SearchSource, type ComputedSuggestions
} from '~/types/algolia/algolia.type';
import type { APIState } from '~/types/api-state.type';
import type { MenuItem } from '~/types/ecom/categories/categories.type';


export const useAlgoliaStore = defineStore({
    id: 'algolia',
    state: () => ({
        algolia: null as AlgoliaService | null,
        searchHistory: <options[]>([]),
        algoliaResponse: <options[]>([]),
        builderResponse: <options[]>([]),
        response: <string[]>([]),
        filter_reviews: [],
        route: null,
        searchItem: <string>(''),
        lastTypedKeyword: <string>(''),
        lastSearchedKeyword: <string>(''),
        isDebounced: <boolean>(false),
        algoliaState: <APIState>({
            status: 'idle',
            message: ''
        }),
        builderState: <APIState>({
            status: 'success',
            message: '',
        }),
        review_count: {
            5: 0,
            4: 0,
            3: 0,
            2: 0,
            1: 0
        },
        delay_time: 1500,
        delay_timer: 0,
        ECOMState: <APIState>({
            status: 'success',
            message: ''
        }),
        numericFilters: [],
        productIDs: <any>([]),
        totalNumOfResults: <number>(0),
        totalNumOfFilteredResults: <number>(0),
        filters: <Filters>undefined,
        filterOrders: [],
        filterCategoryToUpdate: <string[]>([]),
        filterCategoryToUpdateValue: {},

        nbPages: <number>(1),
        currentPage: <number>(0),
        offset: <number>(24),
        sortBy: <string>('default'),
        selectedRating: 0,

        currentActiveIndex: <string>('TSNL_NEW_nl_products'),
        currentActiveCategoryFilter: <string>(''),

        currentFlowParams: ({
            currentActiveSearchFlow: <SearchSource>('direct-search'),
            subCategoryId: <number | undefined>(undefined),
            subCategoryName: <string | undefined>(undefined),
            categorySlug: <string | undefined>(undefined),
        }),
        currentActiveBrand: <string>'',

        currentPLPRoute: <string>'',

        filterCategories: <any>{},

        breadcrumb_data: <any>[],
        currentDropdownStatus: <boolean>(false),
        product_ids_based_on_deal_type: <string[]>([]),
        popularKeywords: <string[]>([]),
        guestToken: <string | null> null,
        redirectUrl:<string>'',
        selectedFilters: <Record<string, string[]>>({}),
        facetsTranslations: {} as Record<string, Record<string, string>>, 
        bannerData:<any>[], 
    }),
    persist: {
        storage: import.meta.client ? localStorage : undefined,
        pick: ['searchHistory', 'offset'],
    },
    actions: {

        async initialize() {
            const { initialize } = await import('~/stores-datalayer/algolia.datalayer');
            return await initialize(this);
        },

        getUserToken(): string {
            if(!this.guestToken){
                const randomId = useRandomUUID();
                this.guestToken = "anonymous-" + randomId;
            }
           const authStore = useAuthStore(); 
           const isAuthenticated = authStore.is_authenticated || authStore.is_guest; 
           return isAuthenticated ? (authStore.user as any).id : this.guestToken;
        },
        
        setFacetsTranslations(cacheKey: string, data: Record<string, string>) {
            this.facetsTranslations[cacheKey] = data;
        },

         getAlgoliaSuggestions(searchKeyword: string) {
            const { getAlgoliaSuggestions } =  import('~/stores-datalayer/algolia.datalayer');
            return  getAlgoliaSuggestions(searchKeyword);

        },

        async getSuggestionsFromAlgolia(searchKeyword: string)
        {
            const { getSuggestionsFromAlgolia } = await import('~/stores-datalayer/algolia.datalayer');
            return await getSuggestionsFromAlgolia(searchKeyword);
        },

        async saveSearchHistoryItem()
        {
            const { saveSearchHistoryItem } =  await import('~/stores-datalayer/algolia.datalayer');
            return  await saveSearchHistoryItem(this);
        },


        handleClearSearchInput() {
            this.isDebounced = false;
            useStateModifier(this.algoliaState, 'idle', 'Idle');
        },


        async handleEnterClick(keyword: string, source: SearchSource,
            doNotNavigate: boolean, facets?: string[][],
            searchIndex?: string, filters?: string,
            subCategoryName?: string, categoryPageId?: number, categorySlug?: string)
        {
            const { handleEnterClick } =  await import('~/stores-datalayer/algolia.datalayer');
            return  await handleEnterClick( keyword, source, doNotNavigate,
                facets, searchIndex, filters,
                subCategoryName, categoryPageId, categorySlug
            );
        },


        async navigationDriver(
            pageSource: SearchSource,
             keyword?: string,
             subCategoryName?: string,
             categoryPageId?: number, categorySlug: string
        )
        {
            const { navigationDriver } =  await import('~/stores-datalayer/algolia.datalayer');
            return  navigationDriver( pageSource, keyword, subCategoryName, categoryPageId, categorySlug);

        },
        async handleBrandRedirect(
            keyword: string,
            doNotNavigate: boolean,
            facets?: string[][],
            searchIndex?: string
        )
        {
            const { handleBrandRedirect } =  await import('~/stores-datalayer/algolia.datalayer');
            return  await handleBrandRedirect(keyword, doNotNavigate, facets, searchIndex);

        },


        async buildSearchResultQuery(query = null)
        {
            const { buildSearchResultQuery } =  await import('~/stores-datalayer/algolia.datalayer');
            return await buildSearchResultQuery( query);
        },


        async buildSearchResultQueryArray()
        {
            const { buildSearchResultQueryArray } = await import('~/stores-datalayer/algolia.datalayer');
            return await buildSearchResultQueryArray(this);
        },


        async computedStateToUpdate() {
            const { computedStateToUpdate } =  await import('~/stores-datalayer/algolia.datalayer');
            return await computedStateToUpdate(this);
        },

        async extractSelectedFilters(facetFilters: string[][]) {
            const { extractSelectedFilters } =  await import('~/stores-datalayer/algolia.datalayer');
            return await extractSelectedFilters( facetFilters);
        },

        async extractSelectedFiltersValues(facetFilters: string[][])
        {
            const { extractSelectedFiltersValues } =  await import('~/stores-datalayer/algolia.datalayer');
            return await extractSelectedFiltersValues(facetFilters);
        },


        async updateFilters(filters: Filters)
        {
            const { updateFilters } =  await import('~/stores-datalayer/algolia.datalayer');
            return await updateFilters(filters);

        },

         extractFiltersFromURL(url: string)
        {
            const { extractFiltersFromURL } =   import('~/stores-datalayer/algolia.datalayer');
            return extractFiltersFromURL( url);
        },




        async setCurrentFlowParams(currentSearchFlow: SearchSource,
                             subCategoryName: string | undefined,
                             subCategoryId: number | undefined,
                             categorySlug: string | undefined)
        {
            const { setCurrentFlowParams } =  await import('~/stores-datalayer/algolia.datalayer');
            await setCurrentFlowParams(currentSearchFlow, subCategoryName, subCategoryId, categorySlug);

        },

        async buildCategoryFilter() {
            const { buildCategoryFilter } =  await import('~/stores-datalayer/algolia.datalayer');
            return buildCategoryFilter(this);
        },


        async setReviewCountByRating() {
            const { setReviewCountByRating } =  await import('~/stores-datalayer/algolia.datalayer');
            return setReviewCountByRating(this);
        },

        async setRatingNumericFilters() {
            const { setRatingNumericFilters } =  await import('~/stores-datalayer/algolia.datalayer');
            return setRatingNumericFilters(this);
        },

        async getFilterQueries(q,query, filters = '', facetFilters = [])
        {
            const { getFilterQueries } =  await import('~/stores-datalayer/algolia.datalayer');
            return await getFilterQueries( q, query, filters, facetFilters);
        },


        /**
         *
         * @param keyword
         * @param source
         * @param doNotNavigate
         * @param facets
         * @param searchIndex
         * @param filters
         * @param subCategoryName
         * @param categoryPageId
         * @param categorySlug
         */


        async createCategoriesFromFilters(facetFilters: Filters)
        {
            const { createCategoriesFromFilters } =  await import('~/stores-datalayer/algolia.datalayer');
            this.filterCategories =  await createCategoriesFromFilters(facetFilters);

        },

        async createSubCategoryBreadCrumb(category: MenuItem | null = null)
        {
            const { createSubCategoryBreadCrumb } =  await import('~/stores-datalayer/algolia.datalayer');
            this.breadcrumb_data = await createSubCategoryBreadCrumb(category);

        },


        /**
         * Category BreadCrumb Builder
         */
        async createCategoryBreadCrumb(id: string)
        {

            const { createCategoryBreadCrumb } =  await import('~/stores-datalayer/algolia.datalayer');
            return await createCategoryBreadCrumb(id);

        },
        async getProductsBasedOnDealType(dealsCategory: string, brandName: string, hitsPerCarousel: number): Promise<string[]>
        {

            const { getProductsBasedOnDealType } =  await import('~/stores-datalayer/algolia.datalayer');
            return await getProductsBasedOnDealType( dealsCategory, brandName, hitsPerCarousel);

        }
    }
});

// Pinia hot reload
if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useAlgoliaStore, import.meta.hot))
}

